/* eslint-disable @typescript-eslint/no-explicit-any */
import { statusFilterParamsOf } from "./utils/resubmission";
import { StatusFilterParams } from "./types";
import { DivergenceStatusEnum } from '@/enum/divergence-status';
import { NsuStatusEnum } from '@/enum/nsu-status';
import { Meta } from '@/@types/paginated';
import { useGetPayrollDiscountNsuQuery } from '@/services/conciliation/nsu';
import { useGetPayrollDiscountDivergencesQuery } from '@/services/divergence';
import { formatFilterValue } from '@/utils/formatters';
import { handeOrderBy } from '@/utils/pagination';
import { Tags } from '@/enum/tags';
import { useManualRefresh } from '@/hooks/use-manual-refresh';
import { MoreHorizontal, RefreshCw } from 'lucide-react';
import { ContentContainer, PageLayout, PageTitle } from 'components/layout';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useColumns } from './components/divergence/columns';
import { useNsuColumns } from './components/nsu/nsu-columns';
import { DataTable } from './components/shared/data-table';
import { DateRangeHeader } from './components/shared/date-range-header';
import { useManageLossFilterState } from './filter/hooks/filterState';
import { DATA_TYPE } from './filter/types';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from '@/components/ui/use-toast';
import { FOREIGN_DECISION_COUNT_TOAST } from './utils/messages';
import {
    DecidableRow,
    decidedElsewhereIds,
} from './utils/foreign-decision';
import { clearOwnDecisions, consumeOwnDecision } from './utils/own-decisions';

const ManageLoss = () => {
    const rowsRef = useRef<readonly DecidableRow[]>([]);

    useEffect((): (() => void) => clearOwnDecisions, []);

    const filterState = useManageLossFilterState();
    const { filters } = filterState || { filters: {} };
    const orderBy = handeOrderBy(filters.order);

    const nsuColumns = useNsuColumns();

    const isDivergenceMode = filters.dataType === DATA_TYPE.DIVERGENCE;

    const getDateFilters = () => {
        if (filters.conciliation_date_range?.start && filters.conciliation_date_range?.end) {
            return {
                conciliation_date_start: filters.conciliation_date_range.start,
                conciliation_date_end: filters.conciliation_date_range.end,
            };
        }
        return {};
    };

    const divergenceStatusFilter: StatusFilterParams<DivergenceStatusEnum> =
        statusFilterParamsOf(filters.status, Object.values(DivergenceStatusEnum));
    const nsuStatusFilter: StatusFilterParams<NsuStatusEnum> =
        statusFilterParamsOf(filters.status, Object.values(NsuStatusEnum));

    // Divergence query
    const { refresh } = useManualRefresh([
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
    ]);

    const { data: divergenceData, isLoading: isDivergenceLoading, isFetching: isDivergenceFetching } = useGetPayrollDiscountDivergencesQuery({
        search: filters.search || '',
        ...getDateFilters(),
        store_code: formatFilterValue(filters.store_code),
        analyst: formatFilterValue(filters.analyst),
        status: divergenceStatusFilter.status,
        resubmission: divergenceStatusFilter.resubmission,
        justification_id: formatFilterValue(filters.justification_id),
        type: formatFilterValue(filters.type),
        payroll_voucher_status: formatFilterValue(
            filters.payroll_voucher_status
        ),
        orderBy: JSON.stringify(orderBy),
        perPage: filters?.meta?.perPage || 10,
        meta: {
            page: filters?.meta?.page || 1,
        },
    }, { skip: !isDivergenceMode });

    const openCycle: string | undefined = divergenceData?.open_cycle;

    const divergenceColumns = useColumns(openCycle);

    // NSU query
    const { data: nsuData, isLoading: isNsuLoading, isFetching: isNsuFetching } = useGetPayrollDiscountNsuQuery({
        search: filters.search || '',
        ...getDateFilters(),
        store_code: formatFilterValue(filters.store_code),
        analyst: formatFilterValue(filters.analyst),
        status: nsuStatusFilter.status,
        resubmission: nsuStatusFilter.resubmission,
        type: formatFilterValue(filters.type),
        orderBy: JSON.stringify(orderBy),
        perPage: filters?.meta?.perPage || 10,
        meta: {
            page: filters?.meta?.page || 1,
        },
    }, { skip: isDivergenceMode });

    const { data: divergences, meta: divergenceMeta } = divergenceData ?? { data: [], meta: {} };

    const rows: readonly DecidableRow[] = useMemo(
        (): readonly DecidableRow[] =>
            (divergences ?? []) as readonly DecidableRow[],
        [divergences]
    );

    useEffect((): void => {
        const foreign: number[] = decidedElsewhereIds(
            rowsRef.current,
            rows
        ).filter((id: number): boolean => !consumeOwnDecision(id));

        if (foreign.length > 0) {
            toast(FOREIGN_DECISION_COUNT_TOAST(foreign.length));
        }

        rowsRef.current = rows;
    }, [rows]);

    const { data: nsus, meta: nsuMeta } = nsuData ?? { data: [], meta: {} };

    const currentData = isDivergenceMode ? divergences : nsus;
    const currentMeta = isDivergenceMode ? divergenceMeta : nsuMeta;
    const currentColumns = isDivergenceMode ? divergenceColumns : nsuColumns;
    const isFirstLoad: boolean = isDivergenceMode
        ? isDivergenceLoading
        : isNsuLoading;
    const isRefetching: boolean = isDivergenceMode
        ? isDivergenceFetching
        : isNsuFetching;

    return (
        <PageLayout>
            <div className="flex w-full items-center justify-between gap-4">
                <PageTitle
                    title="Gerenciar Perdas e Faltas"
                    description="Gerencie as perdas e faltas das divergências e NSUs"
                />
                <div className="flex items-center gap-2 flex-shrink-0">
                    <DateRangeHeader />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="h-9 w-9 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={refresh}
                                disabled={isRefetching}
                            >
                                <RefreshCw
                                    className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
                                />
                                {isRefetching ? 'Atualizando...' : 'Recarregar dados'}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <ContentContainer>
                <DataTable
                    columns={currentColumns}
                    data={currentData}
                    meta={currentMeta as Meta}
                    isLoading={isFirstLoad}
                    dataType={isDivergenceMode ? "divergence" : "nsu"}
                    openCycle={openCycle}
                />
            </ContentContainer>
        </PageLayout>
    )
}

export default ManageLoss
